import { useState } from 'react';
import { Box, Stack, Switch, Typography, useTheme } from '@mui/material';
import { CustomSwitch } from 'components/Inputs';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { disableSystemMessage } from 'services/system-messages.service';
import { getSystemMessagesDisabling } from 'state/reducers/system-messages.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';

interface SystemMessageStatusSwitchProps {
	messageId: number;
	isLive: boolean;
	readOnly?: boolean;
	onDisabled?: () => void;
}

export const SystemMessageStatusSwitch = ({
	messageId,
	isLive,
	readOnly = false,
	onDisabled,
}: SystemMessageStatusSwitchProps) => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const isDisabling = useAppSelector(getSystemMessagesDisabling);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const canDisableMessage = hasPermission([permissionEnums.DISABLE_SYSTEM_MESSAGE]);
	const canDisable = !readOnly && isLive && canDisableMessage;

	const handleDisableConfirm = async () => {
		const result = await dispatch(disableSystemMessage(messageId));
		const disableSucceeded = result && result.success;
		if (disableSucceeded) {
			setConfirmOpen(false);
			if (onDisabled) {
				onDisabled();
			}
		}
	};

	const stopRowClick = (event: React.MouseEvent) => {
		event.stopPropagation();
	};

	return (
		<>
			<Box
				component="span"
				onMouseDown={stopRowClick}
				onClick={stopRowClick}
				sx={{ display: 'inline-flex' }}
			>
				<Stack direction="row" alignItems="center" gap={1.5}>
					<Box sx={{ opacity: canDisable ? 1 : 0.5 }}>
						<CustomSwitch checked={isLive} theme={theme}>
							<Switch
								checked={isLive}
								disabled={!canDisable}
								onClick={stopRowClick}
								onChange={(_event, checked) => {
									if (canDisable && !checked) {
										setConfirmOpen(true);
									}
								}}
							/>
						</CustomSwitch>
					</Box>
					<Typography
						variant="body2"
						sx={{
							color: isLive ? theme.palette.root.forestGreen : theme.palette.root.orange,
							minWidth: 'max-content',
						}}
					>
						{isLive ? t('label.active') : t('page.systemMessages.status.inactive')}
					</Typography>
				</Stack>
			</Box>

			<ConfirmationDialog
				open={!readOnly && confirmOpen}
				title={t('page.systemMessages.disableDialog.title')}
				message={t('page.systemMessages.disableDialog.body')}
				confirmLabel={t('page.systemMessages.disableDialog.confirm')}
				cancelLabel={t('actions.cancel')}
				confirmColor="error"
				disableBackdropClose={true}
				loading={isDisabling}
				onConfirm={handleDisableConfirm}
				onCancel={() => setConfirmOpen(false)}
			/>
		</>
	);
};
